import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import WorkshopModuleNav from '../components/workshop/WorkshopModuleNav';
import WorkshopWorkArea from '../components/workshop/WorkshopWorkArea';
import { API_ENDPOINTS } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import '../styles/events.css';

// Fallback used when the API returns no modules (e.g. not yet seeded for this event)
const DEFAULT_WORKSHOP_MODULES = [
  {
    key: 'pre-workshop',
    title: 'Pre-Workshop',
    lessons: [
      {
        key: 'introduction',
        title: 'Introduction',
        objective: 'Understand workshop goals and expected outcomes.',
        checklist: ['Read workshop overview', 'Confirm account access', 'Set your learning goals'],
      },
      {
        key: 'pre-requisites',
        title: 'Pre-requisites',
        objective: 'Prepare tools and baseline knowledge before workshop day.',
        checklist: ['Install required tools', 'Verify internet and IDE setup', 'Clone starter repository'],
      },
    ],
  },
  {
    key: 'workshop-day',
    title: 'Workshop Day',
    lessons: [
      {
        key: 'case-study',
        title: 'Case Study',
        objective: 'Apply concepts to a realistic business and engineering scenario.',
        checklist: ['Read case context', 'Define problem statement', 'Draft your solution approach'],
      },
      {
        key: 'prompt-labs',
        title: 'Prompt Labs',
        objective: 'Practice iterative prompting patterns with guided exercises.',
        checklist: [],
      },
      {
        key: 'ai-artifacts',
        title: 'AI Artifacts',
        objective: 'Generate and download Context, Prompt, and Agent Instructions from your 5W responses.',
        checklist: [],
      },
    ],
  },
  {
    key: 'post-workshop',
    title: 'Post Workshop',
    lessons: [
      {
        key: 'recording',
        title: 'Recording',
        objective: 'Review key moments from the workshop recording.',
        checklist: ['Watch recording highlights', 'Capture 3 takeaways', 'Share one implementation idea'],
      },
      {
        key: 'important-link',
        title: 'Important Link',
        objective: 'Access references, templates, and follow-up resources.',
        checklist: ['Open resource pack', 'Bookmark templates', 'Plan your 7-day follow-up'],
      },
      {
        key: 'workshop-feedback',
        title: 'Workshop Feedback',
        objective: 'Share your experience and help us improve future workshops.',
        checklist: ['Answer all questions honestly', 'Submit your feedback'],
      },
    ],
  },
];

const getInitialSelection = (modules) => {
  const firstModule = modules[0];
  const firstLesson = firstModule?.lessons[0];
  return { moduleKey: firstModule?.key || '', lessonKey: firstLesson?.key || '' };
};

const isPromptLabsPathname = (pathname = '') => /\/prompt-labs\/?$/.test(pathname);

const getPromptLabsSelection = (modules) => {
  for (const module of modules) {
    const promptLabsLesson = (module?.lessons || []).find((lesson) => lesson?.key === 'prompt-labs');
    if (promptLabsLesson) {
      return { moduleKey: module.key, lessonKey: promptLabsLesson.key };
    }
  }
  return getInitialSelection(modules);
};

const getSelectionForPath = (modules, pathname) => {
  if (isPromptLabsPathname(pathname)) {
    return getPromptLabsSelection(modules);
  }
  return getInitialSelection(modules);
};

const normalizeModules = (rawModules = []) => {
  if (!Array.isArray(rawModules)) return [];

  return rawModules
    .map((module, moduleIndex) => {
      const lessons = Array.isArray(module?.lessons)
        ? module.lessons
            .map((lesson, lessonIndex) => ({
              id:          lesson?.id ?? null,
              key:         lesson?.key || `lesson-${moduleIndex + 1}-${lessonIndex + 1}`,
              title:       lesson?.title || 'Lesson',
              objective:   lesson?.objective || '',
              checklist:   Array.isArray(lesson?.checklist) ? lesson.checklist : [],
              iscompleted: lesson?.iscompleted ?? false,
            }))
            .filter((lesson) => Boolean(lesson.key))
        : [];

      return {
        key:     module?.key || `module-${moduleIndex + 1}`,
        title:   module?.title || 'Module',
        lessons,
      };
    })
    .filter((module) => Boolean(module.key));
};

const WorkshopDetail = ({ workshop: workshopProp, onBackToWorkshops }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workshopId } = useParams();
  const workshop = workshopProp || location.state?.workshop || {};
  const attendeeId = localStorage.getItem('userId') || localStorage.getItem('attendeeId') || '-';
  const attendeeName = localStorage.getItem('attendeeName') || localStorage.getItem('userName') || 'Attendee';
  const attendeeEmail = localStorage.getItem('attendeeEmail') || localStorage.getItem('userEmail') || '';

  const normalizedDefaultModules = useMemo(() => normalizeModules(DEFAULT_WORKSHOP_MODULES), []);
  const [modules, setModules] = useState(() => normalizedDefaultModules);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [selection, setSelection] = useState(() => getSelectionForPath(normalizedDefaultModules, location.pathname));
  const [completedLessons, setCompletedLessons] = useState(() => new Set());

  // Workshop is considered "done" when its end (or start) datetime is in the past.
  // A finished workshop unlocks all lessons so attendees can freely review content.
  const isWorkshopDone = useMemo(() => {
    const raw = workshop.end_datetime || workshop.endDate || workshop.start_datetime || workshop.startDate || workshop.date;
    if (!raw) return false;
    return new Date(raw) < new Date();
  }, [workshop.end_datetime, workshop.endDate, workshop.start_datetime, workshop.startDate, workshop.date]);

  const handleLessonComplete = useCallback((lessonKey) => {
    setCompletedLessons((prev) => {
      if (prev.has(lessonKey)) return prev;
      const next = new Set(prev);
      next.add(lessonKey);
      return next;
    });
  }, []);

  const flatLessons = useMemo(
    () => modules.flatMap((m) => m.lessons.map((l) => ({ moduleKey: m.key, lessonKey: l.key }))),
    [modules]
  );

  const isLessonLocked = useCallback(
    (moduleKey, lessonKey) => {
      // Past workshops are fully unlocked so attendees can freely review all content.
      if (isWorkshopDone) return false;
      const idx = flatLessons.findIndex(
        (fl) => fl.moduleKey === moduleKey && fl.lessonKey === lessonKey
      );
      if (idx <= 0) return false;
      const prev = flatLessons[idx - 1];
      return !completedLessons.has(prev.lessonKey);
    },
    [flatLessons, completedLessons, isWorkshopDone]
  );

  const handleSelect = useCallback(
    (moduleKey, lessonKey) => {
      if (!isLessonLocked(moduleKey, lessonKey)) {
        setSelection({ moduleKey, lessonKey });
      }
    },
    [isLessonLocked]
  );

  useEffect(() => {
    const eventid    = workshop?.id || 0;
    const attendeeid = localStorage.getItem('attendeeId') || localStorage.getItem('userId') || '';
    const params     = new URLSearchParams({ eventid });
    if (attendeeid) params.set('attendeeid', attendeeid);

    setModulesLoading(true);
    fetch(`${API_ENDPOINTS.BASE_URL}/workshops/modules?${params.toString()}`, {
      headers: { [TOKEN_HEADER]: generateToken() },
    })
      .then((res) => res.json())
      .then((payload) => {
        const fetched = normalizeModules(payload?.data);
        if (fetched.length > 0) {
          setModules(fetched);
          setSelection(getSelectionForPath(fetched, location.pathname));
        }
      })
      .catch(() => { /* fallback: keep DEFAULT_WORKSHOP_MODULES */ })
      .finally(() => setModulesLoading(false));
  }, [location.pathname, workshop?.id]);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('attendeeToken');
    localStorage.removeItem('attendeeId');
    localStorage.removeItem('userId');
    localStorage.removeItem('userid');
    localStorage.removeItem('userName');
    localStorage.removeItem('attendeeName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('attendeeEmail');
    localStorage.removeItem('attendeeProfile');
    localStorage.removeItem('userDetails');
    navigate('/events/login?returnTo=/workshop');
  };

  useEffect(() => {
    if (!isPromptLabsPathname(location.pathname)) return;
    setSelection((prev) => {
      if (prev.lessonKey === 'prompt-labs') return prev;
      return getPromptLabsSelection(modules);
    });
  }, [location.pathname, modules]);

  const activeModule = useMemo(
    () => modules.find((m) => m.key === selection.moduleKey) || modules[0],
    [modules, selection.moduleKey]
  );

  const activeLesson = useMemo(
    () =>
      activeModule?.lessons.find((lesson) => lesson.key === selection.lessonKey) ||
      activeModule?.lessons[0],
    [activeModule, selection.lessonKey]
  );

  const workshopTitle = workshop.title || workshop.name || `Workshop ${workshopId}`;

  return (
    <div className="ep-page wk-page">
      <header className="ep-hero wk-hero wk-detail-hero">
        <div className="ep-hero-glow" aria-hidden="true" />
        <div className="ep-hero-inner wk-hero-inner">
          <div className="wk-header-wrap">
            <div className="wk-header-top">
              <span className="ep-live-badge">
                <span className="ep-pulse-dot" />
                Workshop workspace
              </span>

              <div className="wk-header-utility">
                <button
                  className="ep-btn-ghost wk-back-btn"
                  onClick={() => {
                    if (onBackToWorkshops) {
                      onBackToWorkshops();
                      return;
                    }
                    navigate('/workshop');
                  }}
                >
                  ← Back
                </button>

                <div className="wk-user-panel">
                  <span className="wk-user-name">{attendeeName}</span>
                  <button
                    type="button"
                    className="ep-btn-ghost wk-logout-btn"
                    onClick={logout}
                    aria-label="Log out"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
            <h1 className="ep-hero-title wk-title">{workshopTitle}</h1>
            <p className="ep-hero-sub wk-sub">
              Follow the structured path: pre-workshop prep, workshop day execution, and post-workshop reinforcement.
            </p>
         
       
          </div>
        </div>
      </header>

      <section className="wk-detail-section">
        <div className="wk-detail-layout">
          <WorkshopModuleNav
            modules={modules}
            activeModuleKey={selection.moduleKey}
            activeLessonKey={selection.lessonKey}
            completedLessons={completedLessons}
            isLessonLocked={isLessonLocked}
            isWorkshopDone={isWorkshopDone}
            onSelect={handleSelect}
          />

          <WorkshopWorkArea
            workshopTitle={workshopTitle}
            moduleKey={activeModule?.key || ''}
            moduleTitle={activeModule?.title || ''}
            lesson={activeLesson}
            workshopId={workshop.id || workshopId}
            onLessonComplete={handleLessonComplete}
          />
        </div>
      </section>
    </div>
  );
};

export default WorkshopDetail;